import { useState, useRef, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  startOfMonth,
  startOfWeek,
  addDays,
  addMonths,
  subMonths,
  getYear,
  getMonth,
  format,
  isSameDay,
  isToday,
  setYear,
  isValid,
} from 'date-fns';
import ChevronDown from '@/app/components/questionnaireV2/icons/chevron-down';
import ChevronLeft from '@/app/components/questionnaireV2/icons/chevron-left';
import ChevronRight from '@/app/components/questionnaireV2/icons/chevron-right';
import { DATE_CONSTANTS, DAY_NAMES } from '../date-question/date-constants';

interface CalendarDate {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
}

interface DatePickerProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  error: string;
  inputValue: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export function DatePicker({
  selectedDate,
  onDateSelect,
  error,
  inputValue,
  onInputChange,
  placeholder,
}: DatePickerProps) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showYearGrid, setShowYearGrid] = useState(false);
  const [yearGridStart, setYearGridStart] = useState(getYear(new Date()) - 11);

  const calendarRef = useRef<HTMLDivElement>(null);
  const isClickingOutsideRef = useRef(false);

  const { YEARS_PER_PAGE, DAYS_PER_PAGE } = DATE_CONSTANTS;

  // Handle date selection from calendar
  const handleDateSelect = (date: Date) => {
    onDateSelect(date);
    setShowCalendar(false);
  };

  // Handle input field click/focus
  const handleInputFocus = () => {
    // Don't open calendar if we just closed it from clicking outside
    if (isClickingOutsideRef.current) {
      isClickingOutsideRef.current = false;
      return;
    }
    setShowCalendar(true);
    if (selectedDate) {
      setCurrentMonth(selectedDate);
    }
  };

  // Update calendar month when selectedDate changes
  useEffect(() => {
    if (selectedDate && isValid(selectedDate)) {
      setCurrentMonth(selectedDate);
    }
  }, [selectedDate]);

  // Navigation functions
  const goToPreviousMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    if (selectedDate) {
      const newDate = subMonths(selectedDate, 1);
      onDateSelect(newDate);
    }
  };

  const goToNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    if (selectedDate) {
      const newDate = addMonths(selectedDate, 1);
      onDateSelect(newDate);
    }
  };

  const handleYearClick = () => {
    setShowYearGrid(true);
  };

  const handleYearSelect = (year: number) => {
    const newDate = setYear(selectedDate || currentMonth, year);
    setCurrentMonth(newDate);
    if (selectedDate) {
      onDateSelect(newDate);
    }
    setShowYearGrid(false);
  };

  const goToPreviousYearRange = () => {
    setYearGridStart(yearGridStart - YEARS_PER_PAGE);
  };

  const goToNextYearRange = () => {
    const currentYear = getYear(new Date());
    const nextStart = yearGridStart + YEARS_PER_PAGE;
    // Don't go beyond current year
    if (nextStart <= currentYear) {
      setYearGridStart(nextStart);
    }
  };

  const yearGrid = useMemo(() => {
    const years = [];
    const currentYear = getYear(new Date());
    for (let i = 0; i < YEARS_PER_PAGE; i++) {
      const year = yearGridStart + i;
      if (year <= currentYear) {
        years.push(year);
      }
    }
    return years;
  }, [yearGridStart, YEARS_PER_PAGE]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking on the calendar itself
      if (calendarRef.current && calendarRef.current.contains(event.target as Node)) {
        return;
      }

      // Close calendar for any other click (including on the input)
      isClickingOutsideRef.current = true;
      setShowCalendar(false);
      setShowYearGrid(false);

      // Reset the flag after a short delay
      setTimeout(() => {
        isClickingOutsideRef.current = false;
      }, 100);
    };

    if (showCalendar) {
      // Use capture phase to handle the event before any other handlers
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }
  }, [showCalendar]);

  const calendarDates = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);

    const dates: CalendarDate[] = [];

    for (let i = 0; i < DAYS_PER_PAGE; i++) {
      const date = addDays(calendarStart, i);

      dates.push({
        date,
        isCurrentMonth: getMonth(date) === getMonth(currentMonth),
        isToday: isToday(date),
        isSelected: selectedDate ? isSameDay(date, selectedDate) : false,
      });
    }

    return dates;
  }, [currentMonth, selectedDate, DAYS_PER_PAGE]);

  return (
    <div className="date-picker-container">
      {/* Date Input Field */}
      <input
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={onInputChange}
        onFocus={handleInputFocus}
        onClick={handleInputFocus}
        onBlur={onInputChange}
        placeholder={placeholder ?? 'MM/DD/YY'}
        className={`date-input-field ${error ? 'date-input-field-error' : ''}`}
        onKeyDown={handleKeyDown}
      />
      {/* Calendar */}
      <AnimatePresence>
        {showCalendar && (
          <motion.div
            ref={calendarRef}
            key="calendar"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="calendar-container"
          >
            {/* Calendar Header */}
            <div className="calendar-header">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h3 className="calendar-month-title">
                  {showYearGrid
                    ? `${yearGridStart} - ${Math.min(yearGridStart + YEARS_PER_PAGE - 1, getYear(new Date()))}`
                    : format(currentMonth, 'MMMM')}
                </h3>
                {!showYearGrid && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <h3 className="calendar-month-title calendar-year-clickable" onClick={handleYearClick}>
                      {getYear(currentMonth)}
                    </h3>
                    <ChevronDown className="calendar-year-chevron" onClick={handleYearClick} />
                  </div>
                )}
              </div>

              <div className="calendar-nav-button-container">
                <button
                  type="button"
                  onClick={showYearGrid ? goToPreviousYearRange : goToPreviousMonth}
                  className="calendar-nav-button"
                  aria-label={showYearGrid ? 'Previous year range' : 'Previous month'}
                >
                  <ChevronLeft />
                </button>

                <button
                  type="button"
                  onClick={showYearGrid ? goToNextYearRange : goToNextMonth}
                  className="calendar-nav-button"
                  aria-label={showYearGrid ? 'Next year range' : 'Next month'}
                  disabled={showYearGrid && yearGridStart + YEARS_PER_PAGE > getYear(new Date())}
                >
                  <ChevronRight />
                </button>
              </div>
            </div>

            {/* Year Grid */}
            {showYearGrid && (
              <div className="year-grid">
                {yearGrid.map((year) => {
                  const isSelectedYear = year === getYear(currentMonth);

                  let cellClassName = 'year-grid-cell';
                  if (isSelectedYear) cellClassName += ' year-grid-cell-current';

                  return (
                    <button
                      key={year}
                      type="button"
                      onClick={() => handleYearSelect(year)}
                      className={cellClassName}
                      aria-label={`Select year ${year}`}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Calendar Grid */}
            {!showYearGrid && (
              <div className="calendar-grid">
                {/* Day Headers */}
                {DAY_NAMES.map((day) => (
                  <div key={day} className="calendar-day-header">
                    {day}
                  </div>
                ))}

                {/* Date Cells */}
                {calendarDates.map((calendarDate, index) => {
                  const { date, isCurrentMonth, isToday, isSelected } = calendarDate;
                  const isDisabled = !isCurrentMonth;

                  let cellClassName = 'calendar-day-cell';
                  if (isSelected) cellClassName += ' calendar-day-cell-selected';
                  else if (isToday) cellClassName += ' calendar-day-cell-today';
                  else if (!isCurrentMonth) cellClassName += ' calendar-day-cell-other-month';
                  if (isDisabled) cellClassName += ' calendar-day-cell-disabled';

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => !isDisabled && handleDateSelect(date)}
                      className={cellClassName}
                      disabled={isDisabled}
                      aria-label={`Select ${format(date, 'EEEE, MMMM do, yyyy')}`}
                    >
                      {format(date, 'd')}
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
